import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, SearchX, X } from "lucide-react";
import { api } from "@/lib/api";
import { useTitle, SERVICE_TYPES, NZ_CITIES } from "@/lib/format";
import SearchBar from "@/components/SearchBar";
import ProviderCard from "@/components/ProviderCard";
import { EmptyState, PageLoader } from "@/components/common";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const FILTER_KEYS = ["category", "min_price", "max_price", "min_rating", "availability", "service_type", "sort"];

const Select = ({ label, value, onChange, options, testId }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 focus:border-sky-500 focus:outline-none">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

function Filters({ params, set, categories }) {
  return (
    <div className="space-y-5" data-testid="filters-panel">
      <Select label="Category" testId="filter-category" value={params.get("category")} onChange={(v) => set("category", v)}
        options={[["", "All categories"], ...categories.map((c) => [c.slug, c.name])]} />
      <Select label="Location / area" testId="filter-city" value={params.get("location")} onChange={(v) => set("location", v)}
        options={[["", "Anywhere in NZ"], ...NZ_CITIES.map((c) => [c, c])]} />
      <div>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Price range (NZD)</span>
        <div className="mt-1.5 flex gap-2">
          <input data-testid="filter-min-price" type="number" min="0" placeholder="Min" defaultValue={params.get("min_price") || ""} onBlur={(e) => set("min_price", e.target.value)} className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm" />
          <input data-testid="filter-max-price" type="number" min="0" placeholder="Max" defaultValue={params.get("max_price") || ""} onBlur={(e) => set("max_price", e.target.value)} className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm" />
        </div>
      </div>
      <Select label="Rating" testId="filter-rating" value={params.get("min_rating")} onChange={(v) => set("min_rating", v)}
        options={[["", "Any rating"], ["4.5", "4.5+ stars"], ["4", "4+ stars"], ["3", "3+ stars"]]} />
      <Select label="Availability" testId="filter-availability" value={params.get("availability")} onChange={(v) => set("availability", v)}
        options={[["", "Any time"], ["today", "Available today"], ["tomorrow", "By tomorrow"], ["week", "Within 7 days"]]} />
      <Select label="Service type" testId="filter-service-type" value={params.get("service_type")} onChange={(v) => set("service_type", v)}
        options={[["", "All types"], ...SERVICE_TYPES.map((t) => [t, t])]} />
    </div>
  );
}

function useSearchFilters() {
  const [params, setParams] = useSearchParams();
  const update = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next);
  };
  return {
    params,
    set: (key, value) => update({ [key]: value }),
    onSearch: ({ q, location }) => update({ q, location }),
    clear: () => setParams({}),
    activeCount: FILTER_KEYS.filter((k) => k !== "sort" && params.get(k)).length,
  };
}

function Results({ results, onClear }) {
  if (!results) return <PageLoader />;
  if (results.length === 0) {
    return (
      <EmptyState testId="no-results" icon={SearchX} title="No providers match your search" text="Try a different service, a broader location, or clear some filters."
        action={<button onClick={onClear} className="rounded-xl bg-sky-600 px-5 py-3 text-sm font-semibold text-white">Clear search</button>} />
    );
  }
  return (
    <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="results-grid">
      {results.map((p, i) => <ProviderCard key={p.id} provider={p} index={i} />)}
    </div>
  );
}

const countLabel = (results) => (results ? `${results.length} provider${results.length === 1 ? "" : "s"} found` : "Searching...");

export default function Search() {
  useTitle("Find a Service");
  const { params, set, onSearch, clear, activeCount } = useSearchFilters();
  const [categories, setCategories] = useState([]);
  const [results, setResults] = useState(null);

  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)).catch(() => setCategories([])); }, []);
  useEffect(() => {
    setResults(null);
    api.get("/providers", { params: Object.fromEntries(params.entries()) }).then(({ data }) => setResults(data)).catch(() => setResults([]));
  }, [params]);

  const cat = categories.find((c) => c.slug === params.get("category"));
  const filterKey = params.toString();

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="search-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{cat ? cat.name : "Discover local services"}</h1>
      <p className="mt-2 text-slate-600">Compare trusted providers, prices and availability.</p>
      <div className="mt-6"><SearchBar key={filterKey} initialQ={params.get("q") || ""} initialLocation={params.get("location") || ""} onSearch={onSearch} compact /></div>
      <div className="mt-8 grid gap-8 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-5">
            <Filters key={filterKey} params={params} set={set} categories={categories} />
            {activeCount > 0 && <button data-testid="clear-filters" onClick={clear} className="mt-5 text-sm font-semibold text-sky-700">Clear all filters</button>}
          </div>
        </aside>
        <div>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-600" data-testid="results-count">{countLabel(results)}</p>
            <div className="flex items-center gap-2">
              <Sheet>
                <SheetTrigger data-testid="mobile-filters-button" className="flex h-11 items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold lg:hidden">
                  <SlidersHorizontal className="h-4 w-4" /> Filters {activeCount > 0 && <span className="rounded-full bg-sky-600 px-1.5 text-xs text-white">{activeCount}</span>}
                </SheetTrigger>
                <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-3xl">
                  <SheetHeader><SheetTitle className="font-heading">Filters</SheetTitle></SheetHeader>
                  <div className="mt-4"><Filters key={filterKey} params={params} set={set} categories={categories} /></div>
                  {activeCount > 0 && <button onClick={clear} className="mt-5 flex items-center gap-1 text-sm font-semibold text-sky-700"><X className="h-4 w-4" />Clear filters</button>}
                </SheetContent>
              </Sheet>
              <select data-testid="sort-select" value={params.get("sort") || "recommended"} onChange={(e) => set("sort", e.target.value)} className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm font-medium">
                <option value="recommended">Recommended</option>
                <option value="rating">Highest rated</option>
                <option value="price_low">Price: low to high</option>
                <option value="price_high">Price: high to low</option>
              </select>
            </div>
          </div>
          <Results results={results} onClear={clear} />
        </div>
      </div>
    </div>
  );
}
